/**
 * SplitPanelUI - 分隔面板组件
 * 支持水平和垂直分隔，可拖动调整占比，支持收起/展开
 */

import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';

/**
 * 分隔面板配置选项
 */
export interface SplitPanelOptions extends ElementOptions {
    /** 分隔方向：水平或垂直 */
    direction?: 'horizontal' | 'vertical';
    /** 初始占比 (0-100) */
    splitRatio?: number;
    /** 分割线大小 */
    splitSize?: number;
    /** 占比改变回调 */
    onResize?: (ratio: number) => void;
    /** 第一面板最小占比 */
    minRatio?: number;
    /** 第二面板最小占比 */
    maxRatio?: number;
    /** 是否显示控制按钮 */
    showButtons?: boolean;
    /** 拖动灵敏度 (0-1)，值越小拖动响应越慢，默认0.5 */
    dragSensitivity?: number;
}

export interface SplitPanelInfo {
    panel: HTMLElement;
    content: HTMLElement;
    collapseBtn: HTMLElement | null;
    isCollapsed: boolean;
    ratio: number;
}

const DEFAULT_DRAG_SENSITIVITY = 0.7; // 默认灵敏度为0.5，值越小拖动响应越慢
const DEFAULT_MIN_RATIO = 1; // 第一面板最小占比
const DEFAULT_MAX_RATIO = 99; // 第二面板最大占比

/**
 * SplitPanelUI - 分隔面板组件类
 */
export class SplitPanelUI {
    private ui: BootstrapUI;
    private container: HTMLElement;
    private panelList: Map<string, SplitPanelInfo> = new Map();
    private splitterList: HTMLElement[] = [];
    private panelKeyOrder: string[] = []; // 记录panel的顺序

    private options: SplitPanelOptions;
    private currentRatio: number;
    private isDragging: boolean = false;
    private draggingSplitterIndex: number = -1;

    // 拖动相关的缓存数据
    private startPos: number = 0; // 拖动开始时的鼠标位置
    private containerSize: number = 0; // 容器尺寸（宽度或高度）
    private startLeftRatio: number = 0; // 拖动开始时的左侧面板占比
    private startRightRatio: number = 0; // 拖动开始时的右侧面板占比
    
    // 事件监听器引用，用于清理
    private moveHandler: ((e: MouseEvent | TouchEvent) => void) | null = null;
    private stopHandler: (() => void) | null = null;

    constructor(options: SplitPanelOptions = {}) {
        this.ui = bootstrapUI;
        this.options = {
            direction: 'vertical',
            splitRatio: 50,
            splitSize: 4,

            minRatio: DEFAULT_MIN_RATIO,
            maxRatio: DEFAULT_MAX_RATIO,
            showButtons: false,
            dragSensitivity: DEFAULT_DRAG_SENSITIVITY, // 默认灵敏度为0.5，值越小拖动响应越慢
            ...options
        };

        this.currentRatio = this.options.splitRatio || 50;
        this.container = this.createElement();
    }

    /**
     * 创建分隔面板元素
     */
    private createElement(): HTMLElement {
        const container = this.ui.createElement({
            className: ['vision-split-panel', `vision-split-panel-${this.options.direction}`]
        });

        return container;
    }

    private createPanel(): HTMLElement {
        const panel = this.ui.createElement({
            className: 'vision-split-panel-panel'
        });

        const panelContent = this.ui.createElement({
            className: 'vision-split-panel-content'
        });
        panel.appendChild(panelContent);
        
        return panel;
    }

    /**
     * 创建分割线
     */
    private createSplitter(index: number): HTMLElement {
        const splitter = this.ui.createElement({
            className: 'vision-split-panel-splitter'
        });
        
        // 鼠标事件
        splitter.addEventListener('mousedown', (e) => {
            this.startDrag(e, index);
        });
        
        // 触摸事件支持
        splitter.addEventListener('touchstart', (e) => {
            this.startDrag(e, index);
        });
        
        return splitter;
    }

    /**
     * 获取鼠标/触摸位置（支持触摸事件）
     */
    private getMousePosition(e: MouseEvent | TouchEvent): number {
        if ('touches' in e && e.touches.length > 0) {
            return this.options.direction === 'vertical' 
                ? e.touches[0].clientX 
                : e.touches[0].clientY;
        }
        const mouseEvent = e as MouseEvent;
        return this.options.direction === 'vertical' 
            ? mouseEvent.clientX 
            : mouseEvent.clientY;
    }

    /**
     * 开始拖动
     */
    private startDrag(e: MouseEvent | TouchEvent, splitterIndex: number): void {
        // 右键点击不启动拖动
        if ('button' in e && (e as MouseEvent).button !== 0) {
            return;
        }

        this.isDragging = true;
        this.draggingSplitterIndex = splitterIndex;
        
        // 计算容器尺寸（缓存以避免重复计算）
        const containerRect = this.container.getBoundingClientRect();
        this.containerSize = this.options.direction === 'vertical' 
            ? containerRect.width 
            : containerRect.height;

        // 获取初始鼠标位置
        this.startPos = this.getMousePosition(e);

        // 保存拖动开始时的初始占比（用于计算，不更新panelInfo.ratio）
        const leftPanel = this.panelList.get(this.panelKeyOrder[splitterIndex]);
        const rightPanel = this.panelList.get(this.panelKeyOrder[splitterIndex + 1]);
        
        if (leftPanel && rightPanel) {
            const leftFlexBasis = leftPanel.panel.style.flexBasis;
            const rightFlexBasis = rightPanel.panel.style.flexBasis;
            this.startLeftRatio = leftFlexBasis ? parseFloat(leftFlexBasis) : 0;
            this.startRightRatio = rightFlexBasis ? parseFloat(rightFlexBasis) : 0;
        }

        // 创建绑定的事件处理器
        this.moveHandler = (event: MouseEvent | TouchEvent) => {
            this.onDrag(event);
        };
        this.stopHandler = () => {
            this.stopDrag();
        };

        // 在window上添加事件监听器，确保即使鼠标移出元素也能继续拖动
        window.addEventListener('mousemove', this.moveHandler);
        window.addEventListener('touchmove', this.moveHandler);
        window.addEventListener('mouseup', this.stopHandler);
        window.addEventListener('touchend', this.stopHandler);
        window.addEventListener('touchcancel', this.stopHandler);

        // 禁用文本选择和拖拽（使用上面已声明的 leftPanel 和 rightPanel）
        
        if (leftPanel && leftPanel.panel) {
            leftPanel.panel.style.userSelect = 'none';
            leftPanel.panel.style.webkitUserSelect = 'none';
            leftPanel.panel.style.pointerEvents = 'none';
            leftPanel.panel.addEventListener('selectstart', this.preventDefault);
            leftPanel.panel.addEventListener('dragstart', this.preventDefault);
        }
        
        if (rightPanel && rightPanel.panel) {
            rightPanel.panel.style.userSelect = 'none';
            rightPanel.panel.style.webkitUserSelect = 'none';
            rightPanel.panel.style.pointerEvents = 'none';
            rightPanel.panel.addEventListener('selectstart', this.preventDefault);
            rightPanel.panel.addEventListener('dragstart', this.preventDefault);
        }

        // 设置光标样式
        const cursor = this.options.direction === 'vertical' ? 'col-resize' : 'row-resize';
        const splitter = this.splitterList[splitterIndex];
        if (splitter) {
            splitter.style.cursor = cursor;
        }
        this.container.style.cursor = cursor;
        document.body.style.cursor = cursor;

        this.container.classList.add('resizing');
        e.preventDefault();
    }

    /**
     * 阻止默认行为的辅助函数
     */
    private preventDefault = (e: Event) => {
        e.preventDefault();
    }

    /**
     * 拖动中
     */
    private onDrag(e: MouseEvent | TouchEvent): void {
        if (!this.isDragging || this.draggingSplitterIndex < 0) return;

        // 获取当前鼠标位置
        const mousePos = this.getMousePosition(e);
        
        // 计算鼠标移动的像素距离
        const deltaPixels = mousePos - this.startPos;
        
        // 将像素距离转换为百分比变化，并应用灵敏度系数
        const sensitivity = this.options.dragSensitivity || DEFAULT_DRAG_SENSITIVITY;
        const deltaRatio = (deltaPixels / this.containerSize) * 100 * sensitivity;

        // 基于初始占比计算新占比
        this.applyDragRatio(deltaRatio);
    }

    /**
     * 停止拖动
     */
    private stopDrag(): void {
        if (!this.isDragging) return;

        // 移除window上的事件监听器
        if (this.moveHandler) {
            window.removeEventListener('mousemove', this.moveHandler);
            window.removeEventListener('touchmove', this.moveHandler);
        }
        if (this.stopHandler) {
            window.removeEventListener('mouseup', this.stopHandler);
            window.removeEventListener('touchend', this.stopHandler);
            window.removeEventListener('touchcancel', this.stopHandler);
        }

        // 恢复文本选择和拖拽
        if (this.draggingSplitterIndex >= 0 && this.draggingSplitterIndex < this.panelKeyOrder.length - 1) {
            const leftPanel = this.panelList.get(this.panelKeyOrder[this.draggingSplitterIndex]);
            const rightPanel = this.panelList.get(this.panelKeyOrder[this.draggingSplitterIndex + 1]);
            
            if (leftPanel && leftPanel.panel) {
                leftPanel.panel.style.userSelect = '';
                leftPanel.panel.style.webkitUserSelect = '';
                leftPanel.panel.style.pointerEvents = '';
                leftPanel.panel.removeEventListener('selectstart', this.preventDefault);
                leftPanel.panel.removeEventListener('dragstart', this.preventDefault);
            }
            
            if (rightPanel && rightPanel.panel) {
                rightPanel.panel.style.userSelect = '';
                rightPanel.panel.style.webkitUserSelect = '';
                rightPanel.panel.style.pointerEvents = '';
                rightPanel.panel.removeEventListener('selectstart', this.preventDefault);
                rightPanel.panel.removeEventListener('dragstart', this.preventDefault);
            }
        }

        // 恢复光标样式
        if (this.draggingSplitterIndex >= 0 && this.splitterList[this.draggingSplitterIndex]) {
            this.splitterList[this.draggingSplitterIndex].style.cursor = '';
        }
        this.container.style.cursor = '';
        document.body.style.cursor = '';

        // 清理状态
        this.isDragging = false;
        this.draggingSplitterIndex = -1;
        this.moveHandler = null;
        this.stopHandler = null;
        this.container.classList.remove('resizing');
    }

    /**
     * 应用拖动的占比变化
     */
    private applyDragRatio(deltaRatio: number): void {
        if (this.panelKeyOrder.length < 2 || this.draggingSplitterIndex < 0) return;

        // 获取被拖动的splitter两侧的panel
        const leftKey = this.panelKeyOrder[this.draggingSplitterIndex];
        const rightKey = this.panelKeyOrder[this.draggingSplitterIndex + 1];
        
        const leftPanel = this.panelList.get(leftKey);
        const rightPanel = this.panelList.get(rightKey);

        if (!leftPanel || !rightPanel) return;

        // 基于拖动开始时的初始占比计算新占比（而不是累加）
        const totalRatio = this.startLeftRatio + this.startRightRatio;
        let newLeftRatio = this.startLeftRatio + deltaRatio;
        let newRightRatio = totalRatio - newLeftRatio;

        // 限制范围
        const minRatio = this.options.minRatio || DEFAULT_MIN_RATIO;
        const maxRatio = this.options.maxRatio || DEFAULT_MAX_RATIO;
        
        // 确保不超过最小和最大占比
        if (newLeftRatio < minRatio) {
            newLeftRatio = minRatio;
            newRightRatio = totalRatio - minRatio;
        } else if (newLeftRatio > maxRatio) {
            newLeftRatio = maxRatio;
            newRightRatio = totalRatio - maxRatio;
        }
        
        // 确保右侧也不小于最小值
        if (newRightRatio < minRatio) {
            newRightRatio = minRatio;
            newLeftRatio = totalRatio - minRatio;
        }

        // 更新样式
        leftPanel.panel.style.flexBasis = `${newLeftRatio}%`;
        rightPanel.panel.style.flexBasis = `${newRightRatio}%`;
        leftPanel.ratio = newLeftRatio;
        rightPanel.ratio = newRightRatio;

        // 更新当前占比（使用第一个panel的占比）
        if (this.draggingSplitterIndex === 0) {
            this.currentRatio = newLeftRatio;
        }

        // 触发回调
        if (this.options.onResize) {
            this.options.onResize(newLeftRatio);
        }

        // 触发自定义事件
        this.dispatchPanelResizeEvent(newLeftRatio);
    }

    /**
     * 应用占比（内部方法）- 用于2个panel的情况
     */
    private applyRatio(ratio: number): void {
        // 限制范围
        ratio = Math.max(this.options.minRatio || DEFAULT_MIN_RATIO, Math.min(this.options.maxRatio || DEFAULT_MAX_RATIO, ratio));
        this.currentRatio = ratio;

        // 更新样式（假设只有2个panel）
        if (this.panelKeyOrder.length === 2) {
            const panel1 = this.panelList.get(this.panelKeyOrder[0]);
            const panel2 = this.panelList.get(this.panelKeyOrder[1]);
            if (panel1 && panel2) {
                panel1.panel.style.flexBasis = `${ratio}%`;
                panel2.panel.style.flexBasis = `${100 - ratio}%`;
                panel1.ratio = ratio;
                panel2.ratio = 100 - ratio;
            }
        }

        // 触发回调
        if (this.options.onResize) {
            this.options.onResize(ratio);
        }

        // 触发自定义事件
        this.dispatchPanelResizeEvent(ratio);
    }

    /**
     * 公开的设置占比方法
     */
    public setRatio(ratio: number): void {
        this.applyRatio(ratio);
    }

    /**
     * 触发自定义事件
     */
    private dispatchPanelResizeEvent(ratio: number): void {
        const event = new CustomEvent('panelresize', {
            detail: {
                ratio: ratio,
                direction: this.options.direction,
                element: this.container
            }
        });
        window.dispatchEvent(event);
    }

    /**
     * 新增子panel
     */
    public addPanel(id: string, content: HTMLElement | string): boolean {
        console.log("addPanel----", id, content);
        console.log("panelList----", this.panelList);
        // 检查是否已满
        if (this.panelList.size >= 2) {
            console.warn('SplitPanel: 已满，最多支持2个子面板');
            return false;
        }

        // 创建新的panel
        const panel = this.createPanel();
        const panelContent = panel.querySelector('.vision-split-panel-content') as HTMLElement;
        
        // 设置内容
        if (panelContent) {
            if (typeof content === 'string') {
                panelContent.innerHTML = content;
            } else {
                panelContent.appendChild(content);
            }
        }

        // 保存panel信息
        const panelInfo: SplitPanelInfo = {
            panel: panel,
            content: panelContent,
            collapseBtn: null,
            isCollapsed: false,
            ratio: 0
        };
        this.panelList.set(id, panelInfo);
        this.panelKeyOrder.push(id);

        // 更新container布局
        this.updateContainerLayout();

        return true;
    }

    /**
     * 删除子panel
     */
    public removePanel(isLeft: boolean): boolean {
        // 检查panel数量
        if (this.panelList.size <= 1) {
            console.warn('SplitPanel: 至少需要保留1个子面板');
            return false;
        }

        // 确定要删除的panel key
        let keyToRemove: string;
        if (isLeft) {
            keyToRemove = this.panelKeyOrder[0];
        } else {
            keyToRemove = this.panelKeyOrder[this.panelKeyOrder.length - 1];
        }

        // 从列表中移除
        this.panelList.delete(keyToRemove);
        this.panelKeyOrder = this.panelKeyOrder.filter(key => key !== keyToRemove);

        // 更新container布局（会自动处理DOM和splitter的重建）
        this.updateContainerLayout();

        return true;
    }

    /**
     * 更新container布局
     */
    private updateContainerLayout(): void {
        // 清空container和splitter列表
        this.container.innerHTML = '';
        this.splitterList = [];

        const panelCount = this.panelList.size;
        
        if (panelCount === 0) {
            // 无panel，什么都不显示
            return;
        } else if (panelCount === 1) {
            // 只有一个panel，占满整个容器
            const key = this.panelKeyOrder[0];
            const panelInfo = this.panelList.get(key);
            if (panelInfo) {
                panelInfo.panel.style.flexBasis = '100%';
                panelInfo.ratio = 100;
                this.container.appendChild(panelInfo.panel);
            }
        } else {
            // 两个panel，使用当前占比
            const ratio = this.currentRatio || this.options.splitRatio || 50;
            this.panelKeyOrder.forEach((key, index) => {
                const panelInfo = this.panelList.get(key);
                if (panelInfo) {
                    if (index === 0) {
                        panelInfo.panel.style.flexBasis = `${ratio}%`;
                        panelInfo.ratio = ratio;
                    } else {
                        panelInfo.panel.style.flexBasis = `${100 - ratio}%`;
                        panelInfo.ratio = 100 - ratio;
                    }
                    this.container.appendChild(panelInfo.panel);

                    // 在两个panel之间添加splitter
                    if (index < panelCount - 1) {
                        const splitter = this.createSplitter(index);
                        this.splitterList.push(splitter);
                        this.container.appendChild(splitter);
                    }
                }
            });
            // 确保 currentRatio 已设置
            if (!this.currentRatio) {
                this.currentRatio = ratio;
            }
        }
    }

    /**
     * 设置面板内容
     */
    public setPanelContent(id: string, content: HTMLElement | string): void {
        const panelInfo = this.panelList.get(id);
        if (panelInfo && panelInfo.content) {
            panelInfo.content.innerHTML = '';
            if (typeof content === 'string') {
                panelInfo.content.innerHTML = content;
            } else {
                panelInfo.content.appendChild(content);
            }
        }
    }

    /**
     * 获取容器元素
     */
    public getContainer(): HTMLElement {
        return this.container;
    }

    /**
     * 获取面板内容容器
     */
    public getPanelContent(id: string): HTMLElement | null {
        const panelInfo = this.panelList.get(id);
        return panelInfo?.content || null;
    }

    /**
     * 获取当前占比
     */
    public getRatio(): number {
        return this.currentRatio;
    }

    /**
     * 获取面板数量
     */
    public getPanelCount(): number {
        return this.panelList.size;
    }
}
